import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { Mail, Lock, User, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { clearAllAuthData, logAuthState } from '../utils/authDebug';
import { supabase } from '../lib/supabase';
import { testPasswordReset } from '../utils/testPasswordReset';
import { alternativePasswordReset } from '../utils/alternativePasswordReset';

const Login = () => {
  const { signIn, isAuthenticated, userProfile, loading } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forceShowLogin, setForceShowLogin] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('Login component - Auth state:', { loading, isAuthenticated, userProfile: !!userProfile });
    logAuthState();
  }, [loading, isAuthenticated, userProfile]);

  // Fallback timeout - if loading takes too long, show the login form
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading && !isAuthenticated) {
        console.warn('Auth loading timeout, showing login form');
        console.log('Clearing auth data and forcing login form...');
        clearAllAuthData();
        setForceShowLogin(true);
      }
    }, 5000); // 5 seconds timeout

    return () => clearTimeout(timeout);
  }, [loading, isAuthenticated]);

  // Redirect if already authenticated based on role
  if (isAuthenticated && userProfile) {
    console.log('User authenticated, redirecting based on role:', userProfile.role);
    if (userProfile.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (userProfile.role === 'client') {
      return <Navigate to="/support" replace />;
    } else if (userProfile.role === 'student') {
      return <Navigate to="/dashboard" replace />;
    } else {
      // Default fallback for unknown roles
      return <Navigate to="/dashboard" replace />;
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await signIn(formData.email, formData.password);
      
      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          toast.error('Invalid email or password. Please check your credentials.');
        } else if (error.message.includes('Email not confirmed')) {
          toast.error('Please check your email and click the confirmation link before signing in.');
        } else {
          toast.error(error.message || 'Sign in failed. Please try again.');
        }
      } else {
        toast.success('Welcome back!');
        // Navigation will happen automatically via AuthContext
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      toast.error('Please enter your email address first');
      return;
    }

    setIsResettingPassword(true);

    try {
      // Use the alternative password reset method that works around network issues
      const result = await alternativePasswordReset(formData.email);
      
      if (result.success) {
        toast.success(result.message + ` (Using ${result.redirectUrl})`);
        console.log('Password reset successful with:', result.redirectUrl);
      } else {
        console.error('Alternative password reset failed:', result.error);
        toast.error(result.message);
        
        // Fallback to direct method if alternative fails
        console.log('Trying direct method as fallback...');
        const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
          redirectTo: `http://localhost:3000/reset-password`
        });
        
        if (!error) {
          toast.success('Password reset email sent! Check your inbox.');
        } else {
          toast.error('Failed to send password reset email. Please try again later.');
        }
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error('An error occurred while sending the reset email');
    } finally {
      setIsResettingPassword(false);
    }
  };

  if (loading && !forceShowLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-azellar-light via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-azellar-teal border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-azellar-light via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full"
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5 }}
              className="w-16 h-16 bg-gradient-to-r from-azellar-navy to-azellar-teal rounded-xl flex items-center justify-center mx-auto mb-4"
            >
              <User className="w-8 h-8 text-white" />
            </motion.div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Welcome Back</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">Sign in to your Azellar Academy account</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-azellar-teal focus:border-transparent"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right mb-4">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={isResettingPassword}
                className="text-sm text-azellar-teal hover:text-azellar-navy transition-colors disabled:opacity-50"
              >
                {isResettingPassword ? 'Sending...' : 'Forgot your password?'}
              </button>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'btn-primary hover:transform hover:scale-105'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                  Signing In...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 dark:text-gray-300">
              Don't have an account?{' '}
              <Link 
                to="/auth/signup" 
                className="text-azellar-teal hover:text-azellar-navy font-semibold transition-colors"
              >
                Sign up here
              </Link>
            </p>
          </div>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Link 
              to="/" 
              className="text-gray-500 dark:text-gray-400 hover:text-azellar-teal text-sm transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;